// apps/web/src/ui/Menu.tsx
// Portal-based <Menu> primitive — fixes row-actions clipping inside
// scrollable containers (the original `<DataTable>` `overflow: auto`
// wrapper used to clip the absolute-positioned popup).
//
// Menu owns its open state, positioning math, focus management, keyboard
// nav (ArrowUp/Down/Home/End/Enter/Space), and the close-and-focus split
// (Esc closes + returns focus to trigger; Tab/Shift+Tab close without
// returning focus, letting the browser advance naturally).
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/** Synthetic input shape for {@link computeMenuPosition}. */
export interface ComputeMenuPositionInput {
  anchorRect: { left: number; right: number; top: number; bottom: number };
  menuSize: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Inset from each viewport edge (default 8). */
  edgePadding?: number;
}

/** Result of {@link computeMenuPosition}. */
export interface MenuPosition {
  top: number;
  left: number;
  maxHeight: number;
}

/**
 * @aiPurpose pure positioning math for {@link Menu} — testable without DOM.
 * @aiInvariant returned `{top, left}` keeps the menu at least `edgePadding`
 *   inside the viewport on both horizontal edges (when feasible); flips
 *   above the anchor whenever `bottomGap < menuHeight` (regardless of
 *   `topGap`), with `maxHeight` bounded by the chosen gap minus
 *   `edgePadding` and `top` clamped to `edgePadding` when neither side
 *   fits the full menu height.
 */
export function computeMenuPosition({
  anchorRect,
  menuSize,
  viewport,
  edgePadding = 8,
}: ComputeMenuPositionInput): MenuPosition {
  const { width: vw, height: vh } = viewport;
  const { width: mw, height: mh } = menuSize;

  const bottomGap = vh - anchorRect.bottom;
  const topGap = anchorRect.top;

  let top: number;
  let availableHeight: number;
  // Spec literal: flip whenever `bottomGap < menuHeight` (not only when
  // topGap > bottomGap). When neither side fits, top-end is still chosen
  // and the menu's `max-height: availableHeight - edgePadding` + internal
  // `overflow-y: auto` absorbs the remainder.
  if (bottomGap >= mh) {
    top = anchorRect.bottom;
    availableHeight = bottomGap - edgePadding;
  } else {
    top = Math.max(edgePadding, anchorRect.top - mh);
    availableHeight = topGap - edgePadding;
  }

  let left = anchorRect.right - mw;
  if (left < edgePadding) left = edgePadding;
  const rightLimit = vw - edgePadding;
  if (left + mw > rightLimit) left = Math.max(edgePadding, rightLimit - mw);

  return { top, left, maxHeight: Math.max(0, availableHeight) };
}

/** Props injected by {@link Menu} into the consumer-rendered trigger. */
export interface MenuTriggerProps {
  ref: (el: HTMLElement | null) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: () => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
}

export interface MenuProps {
  /** Render-prop for the trigger; spread the supplied props onto a button. */
  trigger: (props: MenuTriggerProps) => ReactNode;
  children: ReactNode;
  /** Min-width of the menu panel in px. Default 180. */
  minWidth?: number | undefined;
}

// WHY: module-scope so the reference is stable across renders and excluded
// from the `onMenuKeyDown` useCallback deps. Living inside the component
// allocated a new string each render and silently busted the callback's
// memoization.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @aiPurpose accessible row/profile actions menu rendered through a portal
 *   so it escapes any ancestor `overflow` clipping.
 * @aiInvariant menu panel mounts into `#popover-host` (singleton) when
 *   present, else `document.body`; `position: fixed` from
 *   `getBoundingClientRect()` of the trigger; vertical flip when below does
 *   not fit; horizontal clamp keeps panel ≥ 8px inside viewport.
 */
export function Menu({ trigger, children, minWidth = 180 }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setPosition(null);
  }, []);

  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false);
    setPosition(null);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const toggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  // Position computation + reposition listeners.
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const t = triggerRef.current;
      const m = menuRef.current;
      if (!t || !m) return;
      const anchorRect = t.getBoundingClientRect();
      const menuSize = { width: m.offsetWidth, height: m.offsetHeight };
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      setPosition(computeMenuPosition({ anchorRect, menuSize, viewport }));
    };

    // First measure runs after DOM commit; menuRef is populated by then.
    reposition();

    const onScroll = () => reposition();
    const onResize = () => reposition();
    // Capture-phase scroll listener catches scrolls inside any ancestor
    // scrollable, not just window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    let ro: ResizeObserver | null = null;
    let rafHandle = 0;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(reposition);
      if (menuRef.current) ro.observe(menuRef.current);
      if (triggerRef.current) ro.observe(triggerRef.current);
    } else {
      const tick = () => {
        reposition();
        rafHandle = requestAnimationFrame(tick);
      };
      rafHandle = requestAnimationFrame(tick);
    }

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      if (rafHandle) cancelAnimationFrame(rafHandle);
    };
  }, [open]);

  // On open: focus first non-disabled menuitem.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      const first = menuRef.current?.querySelector<HTMLElement>(
        '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])',
      );
      first?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Outside-click close (no focus return — natural pointer behavior).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const onTriggerKeyDown = useCallback((e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  const onMenuKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const m = menuRef.current;
      if (!m) return;
      const items = Array.from(m.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
        (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true',
      );
      const active = document.activeElement as HTMLElement | null;
      const idx = active ? items.indexOf(active) : -1;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeAndFocusTrigger();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const trig = triggerRef.current;
        if (!trig) {
          close();
          return;
        }
        // Walk document tabbables to find next/prev relative to the
        // trigger, mirroring the natural Tab order as if the menu had not
        // been open. Filter to elements actually visible in tab order
        // (presence of `disabled` excluded by selector).
        const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
          (el) =>
            el.tabIndex !== -1 &&
            // Exclude elements inside the open menu so Tab does not loop
            // inside it.
            !m.contains(el),
        );
        const triggerIdx = all.indexOf(trig);
        const target =
          triggerIdx === -1
            ? null
            : e.shiftKey
              ? (all[triggerIdx - 1] ?? null)
              : (all[triggerIdx + 1] ?? null);
        // Move focus before unmount so close-driven re-render does not
        // bounce focus to body via the unmounted menuitem.
        if (target) target.focus();
        else if (e.shiftKey) trig.blur();
        close();
        return;
      }
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = items[(idx + 1 + items.length) % items.length];
        next?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = items[(idx - 1 + items.length) % items.length];
        prev?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        items[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        items[items.length - 1]?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        // Browsers' native button activation on Enter/Space synthesises a
        // click, but jsdom does NOT — so we synthesise one explicitly. This
        // also defends against environments where the focused element is
        // not a native <button> (custom items via a future MenuItem
        // variant). preventDefault stops Space from scrolling.
        e.preventDefault();
        if (
          active &&
          active.getAttribute('role') === 'menuitem' &&
          !active.hasAttribute('disabled') &&
          active.getAttribute('aria-disabled') !== 'true'
        ) {
          active.click();
        }
      }
    },
    [close, closeAndFocusTrigger],
  );

  const onMenuClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const item = target.closest('[role="menuitem"]');
      if (!item) return;
      if (item.hasAttribute('disabled') || item.getAttribute('aria-disabled') === 'true') return;
      close();
    },
    [close],
  );

  const portalTarget =
    typeof document !== 'undefined'
      ? (document.getElementById('popover-host') ?? document.body)
      : null;

  const panelStyle: CSSProperties = {
    position: 'fixed',
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    minWidth,
    maxHeight: position?.maxHeight,
    overflowY: 'auto',
    visibility: position ? 'visible' : 'hidden',
    background: 'var(--bg-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--r-2)',
    boxShadow: 'var(--shadow-2)',
    padding: 4,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 50,
    margin: 0,
  };

  return (
    <>
      {trigger({
        ref: (el) => {
          triggerRef.current = el;
        },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
        onClick: toggle,
        onKeyDown: onTriggerKeyDown,
      })}
      {open && portalTarget
        ? createPortal(
            <div
              ref={menuRef}
              id={menuId}
              role="menu"
              tabIndex={-1}
              onKeyDown={onMenuKeyDown}
              onClick={onMenuClick}
              style={panelStyle}
            >
              {children}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
}

export interface MenuItemProps {
  disabled?: boolean | undefined;
  onClick: () => void;
  children: ReactNode;
}

/**
 * @aiPurpose menu entry rendered inside {@link Menu}'s portal panel.
 * @aiInvariant carries `role="menuitem"`; when `disabled`, sets both
 *   `disabled` and `aria-disabled="true"` so AT and the parent's keyboard
 *   nav both skip it.
 */
export function MenuItem({ disabled, onClick, children }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={disabled || undefined}
      aria-disabled={disabled || undefined}
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '6px 10px',
        border: 0,
        background: 'transparent',
        color: disabled ? 'var(--fg-3)' : 'var(--fg-1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13,
        borderRadius: 'var(--r-1)',
        font: 'inherit',
        width: '100%',
      }}
    >
      {children}
    </button>
  );
}
